import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import { getDocumentSeries, createDocumentSeries, updateDocumentSeries, deleteDocumentSeries } from "../../../../../redux/actions/SocietyPortal/DocumentSeriesAction/DocumentSeriesAction";
import { getInvoiceTypes } from "../../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction";
import { PaginationLimit } from "../../../../../components/PaginationLimit";
import DocumentSeriesModal from "./DocumentSeriesModal";

const DocumentSeries = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Redux state
    const { documentSeriesData, loading, createLoading, updateLoading, deleteLoading, pagination } = useSelector(state => state.DocumentSeriesReducers);
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        document_type: "INVOICE",
        series_name: "",
        invoice_type_id: "",
        prefix: "",
        suffix: "",
        separator: "/",
        include_financial_year: 1,
        financial_year_format: "YY-YY",
        financial_year_start_month: 4,
        start_number: 1,
        number_padding: 4,
        reset_on_financial_year: 1,
        is_default: 0
    });
    const [editId, setEditId] = useState(null);
    const [viewMode, setViewMode] = useState("card");
    const [searchParams, setSearchParams] = useState({
        page: 1,
        limit: 10,
        document_type: "",
        society_id: societyId
    });

    const documentTypes = [
        { value: "INVOICE", label: "Invoice" },
        { value: "CREDIT_NOTE", label: "Credit Note" },
        { value: "DEBIT_NOTE", label: "Debit Note" },
        { value: "RECEIPT", label: "Receipt" },
        { value: "ADVANCE", label: "Advance" },
        { value: "JOURNAL_VOUCHER", label: "Journal Voucher" }
    ];

    const separatorOptions = [
        { value: "/", label: "Slash (/)" },
        { value: "-", label: "Dash (-)" },
        { value: "_", label: "Underscore (_)" },
        { value: "", label: "None" }
    ];

    const financialYearFormats = [
        { value: "YY-YY", label: "YY-YY (e.g., 25-26)" },
        { value: "YYYY-YY", label: "YYYY-YY (e.g., 2025-26)" },
        { value: "YYYY-YYYY", label: "YYYY-YYYY (e.g., 2025-2026)" }
    ];

    const months = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" }
    ];

    // Fetch document series on mount and when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getDocumentSeries(searchParams));
    }, [dispatch, searchParams, refreshKey]);

    // Fetch invoice types for dropdown
    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, limit: 100 }));
    }, [dispatch, societyId]);

    // Generate preview format locally
    const generatePreviewFormat = (data) => {
        const paddedNumber = String(data.start_number || 1).padStart(data.number_padding || 4, '0');
        const sep = data.separator || '';
        let preview = data.prefix || '';

        if (data.include_financial_year && preview) {
            preview += sep;
            // Generate sample financial year
            const currentYear = new Date().getFullYear();
            const nextYear = currentYear + 1;
            if (data.financial_year_format === 'YY-YY') {
                preview += `${String(currentYear).slice(-2)}-${String(nextYear).slice(-2)}`;
            } else if (data.financial_year_format === 'YYYY-YY') {
                preview += `${currentYear}-${String(nextYear).slice(-2)}`;
            } else {
                preview += `${currentYear}-${nextYear}`;
            }
        }

        if (preview && paddedNumber) preview += sep;
        preview += paddedNumber;

        if (data.suffix) preview += sep + data.suffix;

        return preview;
    };

    const TYPES_WITHOUT_INVOICE_TYPE = ['RECEIPT', 'ADVANCE', 'JOURNAL_VOUCHER'];

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            society_id: societyId,
            ...(TYPES_WITHOUT_INVOICE_TYPE.includes(formData.document_type)
                ? { invoice_type_id: null }
                : { invoice_type_id: parseInt(formData.invoice_type_id) }),
            onSuccess: () => {
                handleCloseModal();
                dispatch(getDocumentSeries(searchParams));
            }
        };

        if (editId) {
            dispatch(updateDocumentSeries({ ...payload, id: editId }));
        } else {
            dispatch(createDocumentSeries(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            document_type: item.document_type || "INVOICE",
            series_name: item.series_name || "",
            invoice_type_id: item.invoice_type_id || "",
            prefix: item.prefix || "",
            suffix: item.suffix || "",
            separator: item.separator || "/",
            include_financial_year: item.include_financial_year ?? 1,
            financial_year_format: item.financial_year_format || "YY-YY",
            financial_year_start_month: item.financial_year_start_month || 4,
            start_number: item.start_number || 1,
            number_padding: item.number_padding || 4,
            reset_on_financial_year: item.reset_on_financial_year ?? 1,
            is_default: item.is_default || 0
        });
        setEditId(item.series_id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this document series?")) {
            dispatch(deleteDocumentSeries({
                id,
                society_id: societyId,
                onSuccess: () => {
                    dispatch(getDocumentSeries(searchParams));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            document_type: "INVOICE",
            series_name: "",
            invoice_type_id: "",
            prefix: "",
            suffix: "",
            separator: "/",
            include_financial_year: 1,
            financial_year_format: "YY-YY",
            financial_year_start_month: 4,
            start_number: 1,
            number_padding: 4,
            reset_on_financial_year: 1,
            is_default: 0
        });
        setEditId(null);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    const getDocumentTypeColor = (type) => {
        switch (type) {
            case 'INVOICE': return { bg: '#dcfce7', color: '#16a34a', icon: 'mdi:file-document' };
            case 'CREDIT_NOTE': return { bg: '#fce7f3', color: '#db2777', icon: 'mdi:note-plus' };
            case 'DEBIT_NOTE': return { bg: '#fee2e2', color: '#dc2626', icon: 'mdi:note-minus' };
            case 'RECEIPT': return { bg: '#dbeafe', color: '#2563eb', icon: 'mdi:receipt' };
            case 'ADVANCE': return { bg: '#fef3c7', color: '#d97706', icon: 'mdi:cash-plus' };
            case 'JOURNAL_VOUCHER': return { bg: '#e0e7ff', color: '#4f46e5', icon: 'mdi:book-open' };
            default: return { bg: '#f3f4f6', color: '#6b7280', icon: 'mdi:file' };
        }
    };

    const getDocumentTypeLabel = (type) => {
        return documentTypes.find(dt => dt.value === type)?.label || type;
    };

    const getInvoiceTypeLabel = (invoiceTypeId) => {
        const invoiceType = invoiceTypesData?.find(it => it.invoice_type_id === invoiceTypeId);
        return invoiceType?.invoice_title || "N/A";
    };

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-base">Document Series</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Configure document numbering series for invoices, receipts, and vouchers
                    </p>
                </div>
            </div>

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:information-outline" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> Document series define how document numbers are generated.
                        Format: PREFIX/FY/NUMBER (e.g., INV/25-26/0001)
                    </p>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="card radius-8 border-0 mb-16">
                <div className="card-body p-12">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                Filter by Document Type
                            </label>
                            <select
                                className="form-select radius-8"
                                name="document_type"
                                value={searchParams.document_type}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Document Types</option>
                                {documentTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <button
                                onClick={() => setShowModal(true)}
                                className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                                style={{ backgroundColor: '#f59e0b' }}
                            >
                                <Icon icon="mdi:plus-circle" className="text-base" />
                                Add New Document Series
                            </button>
                        </div>
                        <div className="col-md-4 d-flex justify-content-end">
                            {/* View Toggle */}
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('card')}
                                >
                                    <Icon icon="mdi:view-grid" className="text-lg" />
                                    <span className="d-none d-sm-inline">Card</span>
                                </button>
                                <button
                                    type="button"
                                    className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('list')}
                                >
                                    <Icon icon="mdi:view-list" className="text-lg" />
                                    <span className="d-none d-sm-inline">List</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Card View */}
            {!loading && viewMode === 'card' && (
                <div className="row gy-4">
                    {documentSeriesData?.map((item, index) => {
                        const typeStyle = getDocumentTypeColor(item.document_type);
                        return (
                            <div key={item.series_id} className="col-xxl-4 col-lg-6">
                                <div className="card radius-12 border-0 h-100 shadow-sm hover-shadow-lg transition-all">
                                    {/* Card Header */}
                                    <div className="card-header py-16 px-20 bg-base border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="w-44-px h-44-px rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: typeStyle.bg }}
                                            >
                                                <Icon
                                                    icon={typeStyle.icon}
                                                    className="text-2xl"
                                                    style={{ color: typeStyle.color }}
                                                />
                                            </div>
                                            <div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge bg-neutral-100 text-secondary-light text-xs fw-medium px-10 py-4 radius-4">
                                                        S No.: {String(index + 1).padStart(2, '0')}
                                                    </span>
                                                    {item.is_default === 1 && (
                                                        <span className="badge bg-success-100 text-success-600 text-xs fw-medium px-10 py-4 radius-4">
                                                            Default
                                                        </span>
                                                    )}
                                                </div>
                                                <h6 className="fw-semibold mb-0 mt-1">{item.series_name || getDocumentTypeLabel(item.document_type)}</h6>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(item.series_id)}
                                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                            disabled={deleteLoading}
                                        >
                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                            <span className="d-none d-xl-inline">Delete</span>
                                        </button>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body p-20">
                                        <div className="row gy-3">
                                            {/* Document Type */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Document Type
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={getDocumentTypeLabel(item.document_type)}
                                                    disabled
                                                />
                                            </div>

                                            {/* Billing Type */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Billing Type
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.invoice_type_id ? (item.invoiceType?.invoice_title || getInvoiceTypeLabel(item.invoice_type_id)) : '—'}
                                                    disabled
                                                />
                                            </div>

                                            {/* Prefix */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Prefix
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.prefix || "-"}
                                                    disabled
                                                />
                                            </div>

                                            {/* Suffix */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Suffix
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.suffix || "-"}
                                                    disabled
                                                />
                                            </div>

                                            {/* Current Number */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Current Number
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.current_number || item.start_number || 0}
                                                    disabled
                                                />
                                            </div>

                                            {/* Financial Year */}
                                            <div className="col-6">
                                                <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                    Financial Year
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8 h-44-px bg-neutral-50"
                                                    value={item.include_financial_year ? "Included" : "Not Included"}
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {/* Sample Format Preview */}
                                        <div className="mt-16 p-16 bg-neutral-50 radius-8">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:file-document-outline" className="text-base text-primary-600" />
                                                    <span className="text-xs text-secondary-light">Preview Format</span>
                                                </div>
                                            </div>
                                            <code className="d-block text-center py-8 bg-white radius-4 fw-bold text-lg">
                                                {item.preview_format || generatePreviewFormat(item)}
                                            </code>
                                        </div>

                                        {/* Stats Row */}
                                        <div className="mt-12 p-12 bg-success-50 radius-8">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:counter" className="text-base text-success-600" />
                                                    <span className="text-xs text-secondary-light">Documents Generated</span>
                                                </div>
                                                <span className="fw-bold text-lg text-success-600">
                                                    {item.current_number || 0}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="mt-20 d-flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                            >
                                                <Icon icon="mdi:pencil" className="text-lg" />
                                                <span className="text-sm">Edit Series</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Empty State */}
                    {(!documentSeriesData || documentSeriesData.length === 0) && (
                        <div className="col-12">
                            <div className="card radius-12 border-0 shadow-sm">
                                <div className="card-body p-24 text-center">
                                    <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                        <Icon icon="mdi:numeric" className="text-secondary-light text-2xl" />
                                    </div>
                                    <h6 className="fw-semibold mb-2">No Document Series Added Yet</h6>
                                    <p className="text-secondary-light text-sm mb-20">
                                        Get started by adding your first document series to configure document numbering.
                                    </p>
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                    >
                                        <Icon icon="mdi:plus-circle" className="text-base" />
                                        Add First Document Series
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Series Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Document Type</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Prefix</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Current No.</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Preview Format</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Default</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documentSeriesData?.map((item, index) => {
                                        const typeStyle = getDocumentTypeColor(item.document_type);
                                        return (
                                            <tr key={item.series_id}>
                                                <td className="ps-12 py-8 align-middle">
                                                    <span className="text-secondary-light fw-medium">{String(index + 1).padStart(2, '0')}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="fw-medium">{item.series_name || "-"}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="w-32-px h-32-px rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: typeStyle.bg }}
                                                        >
                                                            <Icon
                                                                icon={typeStyle.icon}
                                                                className="text-lg"
                                                                style={{ color: typeStyle.color }}
                                                            />
                                                        </div>
                                                        <span>{getDocumentTypeLabel(item.document_type)}</span>
                                                    </div>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="badge bg-primary-50 text-primary-600 fw-medium px-12 py-6 radius-4">
                                                        {item.prefix || '-'}
                                                    </span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <span className="fw-bold text-success-600">{item.current_number || item.start_number || 0}</span>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    <code className="bg-neutral-50 px-12 py-6 radius-4">
                                                        {item.preview_format || generatePreviewFormat(item)}
                                                    </code>
                                                </td>
                                                <td className="py-16 align-middle">
                                                    {item.is_default === 1 ? (
                                                        <span className="badge bg-success-100 text-success-600 fw-medium px-12 py-6 radius-4">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">No</span>
                                                    )}
                                                </td>
                                                <td className="pe-12 py-8 align-middle text-end">
                                                    <div className="d-flex align-items-center justify-content-end gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item.series_id)}
                                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                            disabled={deleteLoading}
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                            <span className="d-none d-lg-inline">Delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Empty State for List View */}
                        {(!documentSeriesData || documentSeriesData.length === 0) && (
                            <div className="p-40 text-center">
                                <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                    <Icon icon="mdi:numeric" className="text-secondary-light text-2xl" />
                                </div>
                                <h6 className="fw-semibold mb-2">No Document Series Added Yet</h6>
                                <p className="text-secondary-light text-sm mb-20">
                                    Get started by adding your first document series to configure document numbering.
                                </p>
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                >
                                    <Icon icon="mdi:plus-circle" className="text-base" />
                                    Add First Document Series
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Pagination */}
            {!loading && pagination && pagination.total > 0 && (
                <PaginationLimit
                    currentPage={pagination.page || searchParams.page}
                    totalRecords={pagination.total || 0}
                    limit={searchParams.limit}
                    handleLimitChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })}
                    handlePageChange={(page) => setSearchParams({ ...searchParams, page })}
                />
            )}

            {/* Add/Edit Modal */}
            <DocumentSeriesModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                documentTypes={documentTypes}
                separatorOptions={separatorOptions}
                financialYearFormats={financialYearFormats}
                months={months}
                invoiceTypesData={invoiceTypesData}
                generatePreviewFormat={generatePreviewFormat}
                loading={createLoading || updateLoading}
            />

            {/* Custom Styles */}
            <style>{`
                .hover-shadow-lg:hover {
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
                    transform: translateY(-2px);
                }

                .transition-all {
                    transition: all 0.3s ease;
                }

                .w-32-px { width: 32px; }
                .h-32-px { height: 32px; }
                .w-40-px { width: 40px; }
                .h-40-px { height: 40px; }
                .w-44-px { width: 44px; }
                .h-44-px { height: 44px; }
                .h-48-px { height: 48px; }
                .w-80-px { width: 80px; }
                .h-80-px { height: 80px; }

                .card-body .btn {
                    font-weight: 500;
                }

                .table > :not(caption) > * > * {
                    border-bottom-color: var(--bs-border-color);
                }
            `}</style>
        </div>
    );
};

export default DocumentSeries;
